import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { slugify } from "./utils/slugify";
import { Navigation } from "./Navigation";
import { Footer } from "./Footer";
import { RadialMenu } from "./RadialMenu";
import { useContent } from "../content/ContentProvider";
import { useMediaQuery } from "react-responsive";

export const SkillPage: React.FC = () => {
  const { skillSlug } = useParams<{ skillSlug: string }>();
  const { skillPage } = useContent();
  const skillContents = skillPage.skills;
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [currentSlide, setCurrentSlide] = useState(0);

  // Reset delle slide quando cambia lo skillSlug
  useEffect(() => {
    setCurrentSlide(0);
    window.scrollTo(0, 0);
  }, [skillSlug]); // Aggiunto skillSlug alle dipendenze

  const skillKey = Object.keys(skillContents).find(
    (key) => slugify(key) === skillSlug
  );

  if (!skillKey) {
    return (
      <div className="h-screen flex flex-col">
        <Navigation property={isDesktop ? "desktop" : "mobile"} />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-xl">{skillPage.notFound}</p>
        </main>
        <Footer property={isDesktop ? "desktop" : "mobile"} />
        <RadialMenu />
      </div>
    );
  }

  const { subtitle, description } = skillContents[skillKey];
  const descriptionEntries = Object.entries(description);
  const slides = [];

  for (let i = 0; i < descriptionEntries.length; i += 2) {
    slides.push(descriptionEntries.slice(i, i + 2));
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Navigation property={isDesktop ? "desktop" : "mobile"} />
      <main className="flex-1 bg-white px-4 md:px-8 flex flex-col overflow-hidden">
        <div className="w-full max-w-4xl mx-auto flex flex-col flex-1 pt-20">
          <div className="border-t-2 border-hot-pink pt-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2 text-hot-pink">{skillKey}</h1>
            <h2 className="text-l md:text-2xl italic mb-4 text-purple">{subtitle}</h2>
          </div>
          
          <div className="flex-1 relative min-h-0">
            {slides.map((slide, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-300 overflow-y-auto ${
                  index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                }`}
              >
                {slide.map(([subTitle, items]) => (
                  <div key={subTitle} className="mb-4">
                    <h3 className="text-lg md:text-xl font-semibold mb-2 text-lavender">{subTitle}</h3>
                    <ul className="list-disc list-inside space-y-1">
                      {items.map((item, idx) => (
                        <li key={idx} className="text-dark text-sm md:text-base">{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center py-4 border-t-2 border-hot-pink mt-4">
            <button onClick={prevSlide} className="text-hot-pink hover:text-purple transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex justify-center">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`w-3 h-3 rounded-full mx-1 ${
                    index === currentSlide ? "bg-hot-pink" : "bg-gray-300"
                  }`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
            <button onClick={nextSlide} className="text-hot-pink hover:text-purple transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </main>
      <Footer property={isDesktop ? "desktop" : "mobile"} />
      <RadialMenu />
    </div>
  );
};